/**
 * useBarcodeScanner
 *
 * High-level composable that ties together SSE lifecycle, request coordination,
 * and scanner device selection into a single, reusable interface.
 *
 * This is the composable that individual views / dialogs should consume.
 * They do NOT need to interact with `useScannerSSE` or
 * `useScannerRequestCoordinator` directly.
 *
 * Design for multi-field forms
 * ────────────────────────────
 * A single `useBarcodeScanner()` instance is created per page / dialog.
 * It opens one SSE connection and manages one scanner device.
 * Multiple `<ScanInput>` components share that instance via the `scanner` prop.
 * Each field calls `triggerScanInto(modelRef, onFieldStatus)` — the callback
 * gives the field its own per-scan status so two fields on the same form
 * do not clobber each other's status chips.
 *
 * Scan flow (per field):
 *  1. Dialog opens → call `connectScanner()`
 *  2. User clicks the scan icon next to a field → `ScanInput` calls
 *     `scanner.triggerScanInto(model, (s) => fieldStatus.value = s)`
 *  3. Physical scanner reads barcode → SSE delivers `scan-result`
 *  4. `target.value` is set to the decoded barcode
 *  5. Dialog closes → call `disconnectScanner()`
 *     (also auto-called on component unmount)
 *
 * Simplified usage (single field):
 *   const scanner = useBarcodeScanner({ workflow: 'my-flow' });
 *   await scanner.connectScanner();
 *   // In template: <ScanInput v-model="myField" :scanner="scanner" />
 */
import { ref, onUnmounted, nextTick } from 'vue';
import { useScannerSSE } from './useScannerSSE';
import type { ScannerSseResultEvent } from './useScannerSSE';
import { useScannerRequestCoordinator } from './useScannerRequestCoordinator';
import { requestScannerScan, getConfiguredScannerDevices } from '@/shared/services/device-config.api';
import { config } from '@/core/config';
import type { ScannerUiStatus } from './useScannerRequestCoordinator';
 
export type { ScannerUiStatus };
 
// ── Types ──────────────────────────────────────────────────────────────────
 
/** Minimal writable-ref shape — compatible with Ref<string> and WritableComputedRef<string>. */
export type StringTarget = { value: string };
 
type UseBarcodeScanner = {
  /** Workflow label forwarded in the scan context payload. */
  workflow?: string;
};
 
// ── Barcode extraction helper ──────────────────────────────────────────────
 
/**
 * Extract the decoded barcode string from the opaque `scanJob` payload.
 * Tries commonly used field names in order of precedence.
 */
function extractBarcode(scanJob: unknown): string | null {
  if (!scanJob || typeof scanJob !== 'object') return null;
  const job = scanJob as Record<string, unknown>;
  for (const key of ['scanValue', 'barcodeData', 'decodedValue', 'rawData', 'barcode', 'data', 'value', 'text']) {
    const candidate = job[key];
    if (typeof candidate === 'string' && candidate.trim().length > 0) {
      return candidate.trim();
    }
  }
  return null;
}
 
// ── Composable ─────────────────────────────────────────────────────────────
 
export function useBarcodeScanner({ workflow }: UseBarcodeScanner = {}) {
  /** Error when scanner setup fails (no device / no tenant). */
  const scannerError = ref<string | null>(null);
  /** Persisted scanner deviceId — resolved once on first connect, cached thereafter. */
  let resolvedScannerDeviceId: string | null = null;
 
  const coordinator = useScannerRequestCoordinator(30_000);
 
  /**
   * In-flight scan entries: correlationId → per-field status callback + target ref.
   * Allows multiple concurrent scans (different fields) each to get their own status.
   */
  const inflightFields = new Map<
    string,
    { target: StringTarget; onStatus: (s: ScannerUiStatus) => void }
  >();
 
  // ── SSE handlers ──────────────────────────────────────────────────────
 
  function handleSseResult(event: ScannerSseResultEvent) {
    const cid = event.clientCorrelationId ?? event.correlationId;
    if (!cid) return;
 
    coordinator.resolveFromSse(cid, event);
    const status = coordinator.statusByCorrelation.value.get(cid) ?? 'Idle';
    console.debug(`[BARCODE-SCANNER] SSE scan-result resolved — cid=${cid} status=${status}`);
 
    const entry = inflightFields.get(cid);
    if (entry) {
      entry.onStatus(status);
      if (status === 'Decoded') {
        const barcode = extractBarcode(event.scanJob);
        if (barcode) {
          entry.target.value = barcode;
          console.debug(`[BARCODE-SCANNER] Barcode set on field — cid=${cid} barcode=${barcode}`);
        } else {
          console.warn(`[BARCODE-SCANNER] Decoded status but extractBarcode returned null — cid=${cid}`, event.scanJob);
        }
      }
      inflightFields.delete(cid);
    } else {
      console.warn(`[BARCODE-SCANNER] SSE scan-result has no inflight field entry — cid=${cid} (may have already timed out)`);
    }
  }
 
  function handleSseError(event: ScannerSseResultEvent) {
    const cid = event.clientCorrelationId ?? event.correlationId;
    if (!cid) return;
 
    coordinator.resolveFromSse(cid, event);
    const status = coordinator.statusByCorrelation.value.get(cid) ?? 'Failed';
    console.warn(`[BARCODE-SCANNER] SSE scan-error resolved — cid=${cid} status=${status} error=${event.errorMessage}`);
 
    const entry = inflightFields.get(cid);
    if (entry) {
      entry.onStatus(status);
      inflightFields.delete(cid);
    } else {
      console.warn(`[BARCODE-SCANNER] SSE scan-error has no inflight field entry — cid=${cid} (may have already timed out)`);
    }
  }
 
  // ── SSE instance ──────────────────────────────────────────────────────
 
  const sse = useScannerSSE({
    onScanResult: handleSseResult,
    onScanError: handleSseError,
  });
 
  // ── Public API ────────────────────────────────────────────────────────
 
  /**
   * Connect the SSE stream and resolve the scanner device id.
   * Call this when the consuming component becomes visible / dialog opens.
   */
  async function connectScanner(): Promise<void> {
    scannerError.value = null;

    if (!resolvedScannerDeviceId) {
      try {
        const scanners = await getConfiguredScannerDevices();
        const first = scanners[0];
        if (first) {
          resolvedScannerDeviceId = first.device_id ?? first.deviceId ?? null;
          console.debug(`[BARCODE-SCANNER] Resolved scanner device — deviceId=${resolvedScannerDeviceId}`);
        } else {
          console.warn('[BARCODE-SCANNER] No configured scanner devices found');
        }
      } catch (err) {
        console.warn('[BARCODE-SCANNER] getConfiguredScannerDevices() failed (non-fatal)', err);
      }
    } else {
      console.debug(`[BARCODE-SCANNER] Using cached scanner deviceId=${resolvedScannerDeviceId}`);
    }
    sse.connect();
  }
 
  /**
   * Disconnect the SSE stream and reset scanner state.
   * Call this when the consuming component hides / dialog closes.
   * Also auto-called on component unmount.
   */
  function disconnectScanner(): void {
    sse.disconnect();
    scannerError.value = null;
    inflightFields.clear();
  }
 
  /**
   * Trigger a scan request targeting a specific writable ref.
   *
   * @param target    The ref/model whose `.value` will be set to the decoded barcode.
   * @param onStatus  Per-field status callback — the `ScanInput` component uses this
   *                  to show its own inline status chip independently of other fields.
   *
   * Example (inside ScanInput.vue):
   *   await scanner.triggerScanInto(model, (s) => { localStatus.value = s; });
   */
  async function triggerScanInto(
    target: StringTarget,
    onStatus: (status: ScannerUiStatus) => void,
  ): Promise<void> {
    const tenantId = config.tenantId;
    const userId = config.staffId;
 
    if (!tenantId) {
      scannerError.value = 'Tenant not configured. Please log in again.';
      onStatus('Failed');
      return;
    }
 
    if (!resolvedScannerDeviceId) {
      scannerError.value = 'No scanner device found. Please configure a scanner in Device Settings.';
      onStatus('Failed');
      return;
    }
 
    scannerError.value = null;
    onStatus('Scanning');
 
    const correlationId = crypto.randomUUID();
    inflightFields.set(correlationId, { target, onStatus });
    console.debug(`[BARCODE-SCANNER] Scan requested — correlationId=${correlationId} deviceId=${resolvedScannerDeviceId} tenantId=${tenantId}`);
 
    const completion = coordinator.begin(correlationId);
 
    // On timeout: forward Failed to the field's status callback.
    completion.catch(() => {
      console.warn(`[BARCODE-SCANNER] Scan timed out — correlationId=${correlationId}`);
      const entry = inflightFields.get(correlationId);
      if (entry) {
        entry.onStatus('Failed');
        inflightFields.delete(correlationId);
      }
    });
 
    try {
      await requestScannerScan({
        tenantId,
        deviceId: resolvedScannerDeviceId,
        userId,
        scanMode: 'command',
        fallbackToImageOnFailure: false,
        clientCorrelationId: correlationId,
        context: { workflow: workflow ?? 'barcode-scan' },
      });
      console.debug(`[BARCODE-SCANNER] Scan command accepted (HTTP 2xx) — correlationId=${correlationId}, awaiting SSE result`);
      // HTTP 2xx = command accepted; actual result arrives via SSE.
    } catch (err: unknown) {
      const error = err instanceof Error ? err : new Error(String(err));
      coordinator.failFromHttp(correlationId, error);
      const status = coordinator.statusByCorrelation.value.get(correlationId) ?? 'Failed';
 
      const entry = inflightFields.get(correlationId);
      if (entry) {
        entry.onStatus(status);
        inflightFields.delete(correlationId);
      }
 
      if (status === 'ScannerBusy') {
        scannerError.value = 'Scanner is busy. Please wait and try again.';
      } else if (error.message.includes('503')) {
        scannerError.value = 'No scanner agent connected. Ensure the scan agent is running.';
      } else if (error.message.includes('504')) {
        scannerError.value = 'Scanner command timed out. Please try again.';
      } else {
        scannerError.value = 'Scan failed. Please try again or enter the barcode manually.';
      }
    }
  }
 
  onUnmounted(disconnectScanner);
 
  return {
    /** Whether the SSE connection to the scanner stream is open. */
    isScannerConnected: sse.isConnected,
    /** SSE connection error, if any. */
    sseConnectionError: sse.connectionError,
    /** Scanner setup error (no device / no tenant). Shown at page/dialog level. */
    scannerError,
    connectScanner,
    disconnectScanner,
    triggerScanInto,
  };
}
 
/** Convenience type alias for passing a scanner instance to child components. */
export type BarcodeScannerInstance = ReturnType<typeof useBarcodeScanner>;

/**
 * Wraps a BarcodeScannerInstance so that `onDecoded` is called (via `nextTick`)
 * whenever the scanner status transitions to 'Decoded'.
 *
 * Eliminates the repeated wrapper-object boilerplate in views and dialogs:
 *
 * ```ts
 * const _scanner = useBarcodeScanner({ workflow: 'my-flow' });
 * const myScanner = makeScanner(_scanner, handleScan);
 * ```
 */
export function makeScanner(
  scanner: BarcodeScannerInstance,
  onDecoded: () => void,
): BarcodeScannerInstance {
  return {
    ...scanner,
    triggerScanInto: async (target, onStatus) => {
      await scanner.triggerScanInto(target, (status) => {
        onStatus(status);
        if (status === 'Decoded') {
          nextTick(() => onDecoded());
        }
      });
    },
  };
}